import { useEffect, useRef, useState } from "react";
import { FolderIcon, MoreIcon } from "../icons";
import type { ProjectSummary } from "../types";

/**
 * A conversation's settings, as a menu under a button at the end of the chat header. Copy ID puts the
 * session's id on the clipboard, for another conversation to cite (CONTEXT.md "Conversation reference").
 * Move to project lists the projects it can be filed under, when there are any (ADR-0091).
 */
export function SessionSettings({
	sessionId,
	fileTo = [],
	onFile,
	onError,
}: {
	sessionId: string;
	fileTo?: ProjectSummary[];
	onFile?: (projectId: string) => void;
	onError: (message: string) => void;
}) {
	const [open, setOpen] = useState(false);
	const [copied, setCopied] = useState(false);
	const [choosing, setChoosing] = useState(false);
	const root = useRef<HTMLDivElement>(null);
	// A click anywhere else closes the menu, the way a menu is expected to behave.
	useEffect(() => {
		if (!open) return;
		const away = (e: MouseEvent) => {
			if (!root.current?.contains(e.target as Node)) setOpen(false);
		};
		document.addEventListener("mousedown", away);
		return () => document.removeEventListener("mousedown", away);
	}, [open]);

	async function copyId() {
		try {
			// A page without a clipboard, such as one not served securely, throws here like a refused write.
			await navigator.clipboard.writeText(sessionId);
			setCopied(true);
		} catch {
			setCopied(false);
			onError("复制失败");
		}
	}

	return (
		<div className="session-settings" ref={root}>
			<button
				className="ghost icon-btn"
				aria-label="对话设置"
				title="对话设置"
				aria-expanded={open}
				onClick={() => {
					setOpen((v) => !v);
					setCopied(false);
					setChoosing(false);
				}}
			>
				<MoreIcon size={20} />
			</button>
			{open && (
				<div className="session-menu">
					<button className="ghost" onClick={() => void copyId()}>
						{copied ? "已复制" : "复制 ID"}
					</button>
					{onFile && fileTo.length > 0 && (
						<button className="ghost" aria-expanded={choosing} onClick={() => setChoosing((v) => !v)}>
							移到项目
						</button>
					)}
					{choosing &&
						onFile &&
						fileTo.map((p) => (
							<button
								key={p.id}
								className="ghost project-choice"
								onClick={() => {
									setOpen(false);
									onFile(p.id);
								}}
							>
								<FolderIcon size={15} className="inline-glyph" /> {p.title}
							</button>
						))}
				</div>
			)}
		</div>
	);
}
